import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../Services/api";
import MenuLateral from "../../Componentes/MenuLateral";
import MenuSuperior from "../../Componentes/MenuSuperior";
import Swal from "sweetalert2";
import { PiTreeViewFill } from "react-icons/pi";
import { useAuthContext } from "../../context/authContext";
import { useOperador } from "../../Hooks/useOperador";
import { useNavigate } from "react-router-dom";
import "./index.css";
import { formatCNPJ } from "../../Services/formatCNPJ";

export default function VincularSecaoOperadoresAuditoria() {
  const [clientes, setClientes] = useState([]);
  const [secoes, setSecoes] = useState([]);
  const [operadores, setOperadores] = useState([]);

  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [secaoSelecionada, setSecaoSelecionada] = useState(null);

  const [operadoresInicial, setOperadoresInicial] = useState([]);
  const [operadoresSelecionados, setOperadoresSelecionados] = useState([]);

  const [filtroClientes, setFiltroClientes] = useState("");
  const [filtroSecoes, setFiltroSecoes] = useState("");
  const [filtroOperadores, setFiltroOperadores] = useState("");

  const { authReady } = useAuthContext();
  const { IDOperador, nome, operador } = useOperador();
  const navigate = useNavigate();

  const idFormatado = String(IDOperador).padStart(6, "0");

  useEffect(() => {
    if (!operador && !authReady) navigate("/");
  }, [operador, authReady, navigate]);

  useEffect(() => {
    carregarClientes();
    carregarOperadores();
  }, []);

  const carregarClientes = async () => {
    const res = await api.get("/clientesbyprocesso/Auditoria");
    setClientes(res.data || []);
  };

  const carregarOperadores = async () => {
    const res = await api.get("/operadores");
    setOperadores(res.data || []);
  };

  const carregarSecoes = async (idCliente) => {
    const res = await api.get("/chksecoesstats", {
      params: { IDCliente: idCliente, page: 1, limit: 9999 },
    });

    const lista = res.data?.rows || res.data?.data || res.data || [];
    setSecoes(lista);
  };

  const parseOperadoresResponse = (res) => {
    let lista = [];
    if (Array.isArray(res.data)) lista = res.data;
    else if (Array.isArray(res.data?.rows)) lista = res.data.rows;
    else if (Array.isArray(res.data?.data)) lista = res.data.data;
    return lista.map((o) => o.IDOperador);
  };

  const subSecoesPorPai = useMemo(() => {
    const mapa = new Map();
    secoes.forEach((secao) => {
      if (!secao.IDSecaoPai) return;
      const idPai = Number(secao.IDSecaoPai);
      const atuais = mapa.get(idPai) || [];
      atuais.push(secao);
      mapa.set(idPai, atuais);
    });
    return mapa;
  }, [secoes]);

  const carregarOperadoresDaSecao = useCallback(async (idSecao) => {
    if (!idSecao) {
      setOperadoresInicial([]);
      setOperadoresSelecionados([]);
      return;
    }

    const res = await api.get(`/chksecao/operador/secao/${idSecao}`);
    const ids = parseOperadoresResponse(res);
    setOperadoresInicial(ids);
    setOperadoresSelecionados(ids);
  }, []);

  useEffect(() => {
    setSecoes([]);
    setSecaoSelecionada(null);
    setOperadoresInicial([]);
    setOperadoresSelecionados([]);

    if (clienteSelecionado) carregarSecoes(clienteSelecionado);
  }, [clienteSelecionado]);

  useEffect(() => {
    setOperadoresInicial([]);
    setOperadoresSelecionados([]);

    if (secaoSelecionada) carregarOperadoresDaSecao(secaoSelecionada);
  }, [secaoSelecionada, carregarOperadoresDaSecao]);

  const toggleOperador = (id) => {
    setOperadoresSelecionados((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSalvar = async () => {
    if (!secaoSelecionada) {
      Swal.fire("Atenção", "Selecione uma seção.", "info");
      return;
    }
    const removidos = operadoresInicial.filter(
      (id) => !operadoresSelecionados.includes(id)
    );
    const adicionados = operadoresSelecionados.filter(
      (id) => !operadoresInicial.includes(id)
    );

    try {
      for (const idOperador of removidos) {
        await api.delete(`/chksecao/operador/${secaoSelecionada}/${idOperador}`);
      }

      for (const idOperador of adicionados) {
        await api.post("/chksecao/operador", {
          IDSecao: secaoSelecionada,
          IDOperador: idOperador,
          situacao: 1,
        });
      }

      Swal.fire(
        "Sucesso",
        `Adicionados: ${adicionados.length} | Removidos: ${removidos.length}`,
        "success"
      );

      setOperadoresInicial(operadoresSelecionados);
    } catch (err) {
      console.error(err);
      Swal.fire("Erro", "Falha ao salvar permissões.", "error");
    }
  };

  const clientesFiltrados = clientes.filter((c) =>
    `${c.nome} ${formatCNPJ(c.cnpj)}`
      .toLowerCase()
      .includes(filtroClientes.toLowerCase())
  );

  const termoSecao = filtroSecoes.trim().toLowerCase();
  const secoesRaiz = useMemo(
    () => secoes.filter((s) => !s.IDSecaoPai),
    [secoes]
  );

  const secoesRaizFiltradas = useMemo(() => {
    if (!termoSecao) return secoesRaiz;

    return secoesRaiz.filter((secaoRaiz) => {
      const descricaoRaiz = String(secaoRaiz.descricao || "").toLowerCase();
      if (descricaoRaiz.includes(termoSecao)) return true;

      const subSecoes = subSecoesPorPai.get(Number(secaoRaiz.IDSecao)) || [];
      return subSecoes.some((sub) =>
        String(sub.descricao || "").toLowerCase().includes(termoSecao)
      );
    });
  }, [termoSecao, secoesRaiz, subSecoesPorPai]);

  const operadoresFiltrados = operadores.filter((o) =>
    `${String(o.IDOperador).padStart(6, "0")} ${o.nome}`
      .toLowerCase()
      .includes(filtroOperadores.toLowerCase())
  );

  return (
    <div className="menu-container-menu">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <MenuSuperior userId={idFormatado} userName={nome} />

        <div className="container cadastramento">
          <h2>Vincular Seções (Auditoria) a Operadores</h2>

          <div className="grid">
            {/* CLIENTES */}
            <div className="column">
              <h3>Clientes</h3>
              <input
                className="input-filtro"
                placeholder="Filtrar clientes"
                value={filtroClientes}
                onChange={(e) => setFiltroClientes(e.target.value)}
              />
              <ul>
                {clientesFiltrados.map((c) => (
                  <li key={c.IDCliente}>
                    <label>
                      <input
                        type="radio"
                        checked={clienteSelecionado === c.IDCliente}
                        onChange={() => setClienteSelecionado(c.IDCliente)}
                      />
                      {c.nome} - {formatCNPJ(c.cnpj)}
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* SEÇÕES */}
            <div className="column">
              <h3>Seções</h3>
              <input
                className="input-filtro"
                placeholder="Filtrar Seções"
                value={filtroSecoes}
                onChange={(e) => setFiltroSecoes(e.target.value)}
              />
              <ul>
                {secoesRaizFiltradas.map((secaoRaiz) => {
                  const subSecoes = subSecoesPorPai.get(Number(secaoRaiz.IDSecao)) || [];
                  const grupoCombinaFiltro = termoSecao
                    ? String(secaoRaiz.descricao || "").toLowerCase().includes(termoSecao)
                    : false;
                  const subSecoesFiltradas = termoSecao
                    ? grupoCombinaFiltro
                      ? subSecoes
                      : subSecoes.filter((s) =>
                        String(s.descricao || "").toLowerCase().includes(termoSecao)
                      )
                    : subSecoes;
                  const ehGrupo = subSecoes.length > 0;

                  return (
                    <li key={secaoRaiz.IDSecao}>
                      <label>
                        {ehGrupo ? (
                          <PiTreeViewFill className="icone-grupo-secao" title="Grupo de seções" />
                        ) : (
                          <input
                            type="radio"
                            checked={secaoSelecionada === Number(secaoRaiz.IDSecao)}
                            onChange={() => setSecaoSelecionada(Number(secaoRaiz.IDSecao))}
                          />
                        )}
                        {secaoRaiz.descricao}
                        {ehGrupo ? " (Grupo)" : ""}
                      </label>

                      {ehGrupo && (
                        <ul className="lista-subsecoes">
                          {subSecoesFiltradas.map((subSecao) => (
                            <li key={subSecao.IDSecao}>
                              <label>
                                <input
                                  type="radio"
                                  checked={secaoSelecionada === Number(subSecao.IDSecao)}
                                  onChange={() => setSecaoSelecionada(Number(subSecao.IDSecao))}
                                />
                                {subSecao.descricao}
                              </label>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* OPERADORES */}
            <div className="column">
              <h3>Operadores</h3>
              <input
                className="input-filtro"
                placeholder="Filtrar operadores"
                value={filtroOperadores}
                onChange={(e) => setFiltroOperadores(e.target.value)}
              />
              <ul>
                {operadoresFiltrados.map((o) => {
                  const marcado = operadoresSelecionados.includes(o.IDOperador);
                  return (
                    <li key={o.IDOperador}>
                      <label>
                        <input
                          type="checkbox"
                          checked={marcado}
                          onChange={() => toggleOperador(o.IDOperador)}
                        />
                        {String(o.IDOperador).padStart(6, "0")} - {o.nome}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <button className="btn-salvar" onClick={handleSalvar}>
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
